import { createSelector } from "reselect";
import {
  prop,
  compose,
  reduce,
  curry,
  concat,
  map,
  uniq,
  filter,
  split,
  over,
  lensProp,
  find,
  flatten,
  path,
  pluck,
  chain,
  groupBy,
  head,
  toPairs,
  pick,
  sum,
  divide,
  flip
} from "ramda";
import { formatDate, getDate, tabIndex, getChartData } from "./helpers";
import { Maybe } from "ramda-fantasy";
import moment from "moment";

const listSelector = path(["main", "list"]);
const sitesSelector = compose(listSelector, prop("sites"));
const dailySelector = compose(listSelector, path(["stat", "type", "total"]));
const bySiteStatSelector = compose(
  listSelector,
  path(["stat", "type", "bySite"])
);

const bannerPlacesSelector = path(["sites", "current", "bannerplaces"]);

const listBannerPlacesSelector = compose(prop("list"), bannerPlacesSelector);

const selectedBannerPlacesSelector = path([
  "form",
  "statForm",
  "values",
  "bannerPlaces"
]);

const tabSelector = path(["main", "currentTab"]);
const datesSelector = path(["main", "dates"]);

const listBannerplaces = compose(split(","), x => `${x}`);

const selectedBannerPlaceArray = compose(
  compose(Maybe.maybe("", listBannerplaces), Maybe.toMaybe),
  selectedBannerPlacesSelector
);

const toObject = l =>
  reduce(
    (acc, x) => {
      acc[x.id] = x;
      return acc;
    },
    {},
    l
  );

export const sitesToObject = createSelector(sitesSelector, toObject);
export const dailyToObject = createSelector(dailySelector, list =>
  reduce((acc, x) => {
    acc[x.dynamic_site_id] = x;
    return acc;
  }, {})(list)
);

export const withStat = createSelector(
  [sitesSelector, dailyToObject],
  (sites, stat) => map(site => ({ ...site, list: stat[site.id] }), sites)
);

export const siteWithStat = (state, id) =>
  compose(find(x => x.id === id), withStat)(state);

const renameKeys = curry(({ value, label }) => x => ({
  value: x[value],
  label: x[label]
}));

export const toRenderSelectFormat = createSelector(
  sitesSelector,
  map(renameKeys({ value: "id", label: "name" }))
);

export const toRenderSelectBannerplaces = createSelector(
  listBannerPlacesSelector,
  map(renameKeys({ value: "banner_place", label: "banner_place" }))
);

const filteredList = curry((list, selectedList) =>
  reduce((acc, x) => concat(acc, filter(a => a.banner_place === x)(list)), [])(
    selectedList
  )
);

const formatList = compose(map(x => ({ label: x, value: x })), uniq);

const getFormatList = compose(prop("list"), prop("formats"));

const getFormats = compose(
  // formatList,
  flatten,
  map(getFormatList),
  filteredList
);

export const toRenderFormats = createSelector(
  [listBannerPlacesSelector, selectedBannerPlaceArray],
  getFormats
);

export const toRenderSelectFormats = createSelector(
  toRenderFormats,
  formatList
);

const dateLens = lensProp("date");
const costLens = lensProp("cost");

const formatCost = flip(divide)(1e5);

export const toRenderbySiteStat = createSelector(
  bySiteStatSelector,
  map(over(dateLens, formatDate))
);

// const costLens = lensProp("cost");
// const exposureLens = lensProp("exposure");

// const sumByLens = l => compose(sum, map(view(l)));

const groupByKeys = compose(map(pluck(1)), groupBy(head), chain(toPairs));

export const groupedByKeys = createSelector(
  bySiteStatSelector,
  compose(
    groupByKeys,
    map(over(costLens, formatCost)),
    map(over(dateLens, formatDate))
  )
);

export const calculateSumByFields = createSelector(
  bySiteStatSelector,
  compose(map(sum), pick(["cost", "exposure"]), groupByKeys)
);

export const chartData = createSelector(groupedByKeys, getChartData);

export const getDates = createSelector(
  [tabSelector, datesSelector],
  (tab, dates) =>
    +tab === tabIndex
      ? dates
      : { start: getDate("start", tab), stop: getDate("end", tab) }
);

export const formattedDates = createSelector(getDates, map(moment));
